import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';

const ContactOptions = ({ onContactSubmit }) => {
  const [activeMethod, setActiveMethod] = useState('schedule');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    role: '',
    phone: '',
    urgency: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const contactMethods = [
    {
      id: 'schedule',
      title: 'Schedule Consultation',
      subtitle: 'Book a strategic session',
      description: 'Schedule a comprehensive consultation with our AI transformation experts to discuss your specific needs and develop a custom strategy.',
      icon: 'Calendar',
      color: 'bg-blue-500',
      features: [
        'Strategic planning session',
        'Custom roadmap development',
        'Expert guidance',
        'Follow-up support'
      ]
    },
    {
      id: 'chat',
      title: 'Immediate Chat',
      subtitle: 'Get instant answers',
      description: 'Connect with our AI consultants immediately for urgent questions, quick assessments, or immediate guidance on your AI transformation.',
      icon: 'MessageCircle',
      color: 'bg-green-500',
      features: [
        'Instant response',
        'Quick problem solving',
        'Immediate guidance',
        'No appointment needed'
      ]
    },
    {
      id: 'proposal',
      title: 'Request Proposal',
      subtitle: 'Get custom pricing',
      description: 'Submit your requirements and receive a detailed proposal with custom pricing, timeline, and implementation strategy for your organization.',
      icon: 'FileText',
      color: 'bg-purple-500',
      features: [
        'Custom pricing',
        'Detailed timeline',
        'Implementation strategy',
        'ROI projections'
      ]
    }
  ];

  const roles = [
    { value: 'ceo', label: 'CEO / President' },
    { value: 'cto', label: 'CTO / Technical Leader' },
    { value: 'cio', label: 'CIO / IT Director' },
    { value: 'vp-engineering', label: 'VP Engineering' },
    { value: 'product-manager', label: 'Product Manager' },
    { value: 'business-leader', label: 'Business Unit Leader' },
    { value: 'consultant', label: 'Consultant / Advisor' },
    { value: 'other', label: 'Other' }
  ];

  const urgencyLevels = [
    { value: 'immediate', label: 'Immediate (This week)' },
    { value: 'urgent', label: 'Urgent (Within 2 weeks)' },
    { value: 'normal', label: 'Normal (Within a month)' },
    { value: 'planning', label: 'Planning (2-3 months)' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setIsSubmitting(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    onContactSubmit({
      method: activeMethod,
      data: formData
    });
    
    setIsSubmitting(false);
  };

  const isFormValid = () => {
    return formData?.name && formData?.email && formData?.company && formData?.role;
  };

  const getSubmitText = () => {
    switch (activeMethod) {
      case 'schedule':
        return 'Schedule Consultation';
      case 'chat':
        return 'Start Chat Now';
      case 'proposal':
        return 'Request Proposal';
      default:
        return 'Submit';
    }
  };

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold text-brand-primary mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-text-secondary max-w-3xl mx-auto">
            Choose the engagement method that works best for you. Our AI transformation experts are ready to help you break free from vendor lock-in.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Contact Method Selection */}
          <div className="space-y-6">
            <h3 className="text-2xl font-bold text-brand-primary mb-6">
              Choose Your Preferred Method
            </h3>

            {contactMethods?.map((method) => (
              <div
                key={method?.id}
                className={`cursor-pointer rounded-2xl border-2 transition-all duration-300 ${
                  activeMethod === method?.id
                    ? 'border-brand-primary shadow-executive-lg bg-brand-primary/5'
                    : 'border-gray-200 hover:border-brand-primary/50 bg-white'
                }`}
                onClick={() => setActiveMethod(method?.id)}
              >
                <div className="p-6">
                  <div className="flex items-start space-x-4">
                    <div className={`w-12 h-12 ${method?.color} rounded-xl flex items-center justify-center flex-shrink-0`}>
                      <Icon name={method?.icon} size={24} className="text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-lg font-bold text-brand-primary">
                          {method?.title}
                        </h4>
                        <div className={`w-5 h-5 rounded-full border-2 transition-colors duration-200 ${
                          activeMethod === method?.id
                            ? 'border-brand-primary bg-brand-primary' :'border-gray-300'
                        }`}>
                          {activeMethod === method?.id && (
                            <div className="w-full h-full rounded-full bg-white scale-50" />
                          )}
                        </div>
                      </div>
                      <p className="text-sm text-brand-primary/70 font-medium mb-3">
                        {method?.subtitle}
                      </p>
                      <p className="text-text-secondary text-sm mb-4 leading-relaxed">
                        {method?.description}
                      </p>
                      <div className="grid grid-cols-2 gap-2">
                        {method?.features?.map((feature, index) => (
                          <div key={index} className="flex items-center">
                            <Icon name="Check" size={14} className="text-green-500 mr-2 flex-shrink-0" />
                            <span className="text-xs text-text-primary">{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {/* Trust Signals */}
            <div className="bg-gray-50 rounded-2xl p-6">
              <h4 className="font-semibold text-brand-primary mb-4">Why Choose AI Clean Core?</h4>
              <div className="space-y-3">
                <div className="flex items-center">
                  <Icon name="Shield" size={16} className="text-green-500 mr-3" />
                  <span className="text-sm text-text-primary">500+ successful AI transformations</span>
                </div>
                <div className="flex items-center">
                  <Icon name="Award" size={16} className="text-blue-500 mr-3" />
                  <span className="text-sm text-text-primary">Industry-leading expertise in open-source AI</span>
                </div>
                <div className="flex items-center">
                  <Icon name="TrendingUp" size={16} className="text-purple-500 mr-3" />
                  <span className="text-sm text-text-primary">Average 85% cost reduction achieved</span>
                </div>
                <div className="flex items-center">
                  <Icon name="Clock" size={16} className="text-orange-500 mr-3" />
                  <span className="text-sm text-text-primary">Typical ROI achieved within 6 months</span>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white rounded-2xl shadow-executive p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-brand-primary mb-2">
                  {contactMethods?.find(m => m?.id === activeMethod)?.title}
                </h3>
                <p className="text-text-secondary">
                  Fill out the form below and we'll get back to you within 24 hours
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Full Name"
                  type="text"
                  value={formData?.name}
                  onChange={(e) => handleInputChange('name', e?.target?.value)}
                  placeholder="Enter your full name"
                  required
                />

                <Input
                  label="Business Email"
                  type="email"
                  value={formData?.email}
                  onChange={(e) => handleInputChange('email', e?.target?.value)}
                  placeholder="Enter your business email"
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Company Name"
                  type="text"
                  value={formData?.company}
                  onChange={(e) => handleInputChange('company', e?.target?.value)}
                  placeholder="Enter your company name"
                  required
                />

                <Select
                  label="Your Role"
                  options={roles}
                  value={formData?.role}
                  onChange={(value) => handleInputChange('role', value)}
                  placeholder="Select your role"
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Phone Number"
                  type="tel"
                  value={formData?.phone}
                  onChange={(e) => handleInputChange('phone', e?.target?.value)}
                  placeholder="Enter your phone number"
                />

                <Select
                  label="Timeline"
                  options={urgencyLevels}
                  value={formData?.urgency}
                  onChange={(value) => handleInputChange('urgency', value)}
                  placeholder="Select timeline"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Message (Optional)
                </label>
                <textarea
                  value={formData?.message}
                  onChange={(e) => handleInputChange('message', e?.target?.value)}
                  placeholder="Tell us about your AI challenges and goals..."
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent resize-none"
                />
              </div>

              <Button
                type="submit"
                variant="default"
                size="lg"
                fullWidth
                loading={isSubmitting}
                disabled={!isFormValid() || isSubmitting}
                className="bg-brand-primary"
                iconName={activeMethod === 'chat' ? 'MessageCircle' : activeMethod === 'proposal' ? 'FileText' : 'Calendar'}
                iconPosition="left"
              >
                {isSubmitting ? 'Submitting...' : getSubmitText()}
              </Button>

              <p className="text-xs text-text-secondary text-center">
                By submitting this form, you agree to our privacy policy and terms of service. 
                We'll never share your information with third parties.
              </p>
            </form>
          </div>
        </div>

        {/* Alternative Contact Methods */}
        <div className="mt-16 text-center">
          <h3 className="text-xl font-semibold text-brand-primary mb-6">
            Or reach out directly
          </h3>
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
            <a
              href="tel:+16198001235"
              className="flex items-center space-x-3 text-text-primary hover:text-brand-primary transition-colors duration-200"
            >
              <Icon name="Phone" size={20} />
              <span className="font-medium">1 (619) 800-1235</span>
            </a>
            <a
              href="mailto:hello@aicleancore.com"
              className="flex items-center space-x-3 text-text-primary hover:text-brand-primary transition-colors duration-200"
            >
              <Icon name="Mail" size={20} />
              <span className="font-medium">hello@aicleancore.com</span>
            </a>
            <div className="flex items-center space-x-3 text-text-secondary">
              <Icon name="MapPin" size={20} />
              <span>San Francisco, CA</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactOptions;